import { useMemo, useState } from 'react';
import percent from '~/assets/icons/percent.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Divider } from '~/components/ui/Divider';
import { InputRow } from '~/components/ui/InputRow';
import { MoneyInput } from '~/components/ui/MoneyInput';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction } from '~/rage';
import {
	Currency,
	IModalReplyWithValue,
	IModalStateBusinessWithdraw,
	InputAction,
} from '~/shared';
import { CurrencyIcon } from '../../CurrencyIcon';
import { ModalButton } from '../ModalButton';
import {
	StyledButtonsContainer,
	StyledDescription,
	StyledInputs,
	StyledModalBody,
	StyledPercentIcon,
} from './ModalBusinessWithdraw.styled';

const ModalBusinessWithdraw: React.FC<{
	state: IModalStateBusinessWithdraw;
}> = ({ state }) => {
	const currencyIcon = useWalletIcon(Currency.Ruble, true);

	const [amount, setAmount] = useState<number>(0);

	const taxPercent = state?.taxPercent ?? 0;
	const balance = state?.balance ?? 0;

	const payout = useMemo(
		() => Math.floor(amount * (1 - taxPercent / 100)),
		[amount, taxPercent],
	);

	const reply = (isAccepted: boolean) => {
		rage.modal.replyToCurrentModal({
			isAccepted,
			value: isAccepted ? amount : 0,
		} as IModalReplyWithValue<number>);
	};

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (amount > 0) {
			reply(true);
		}
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		reply(false);
	});

	const renderCurrencyIcon = (size: 'sm' | 'md' | 'lg' = 'sm') => (
		<CurrencyIcon icon={currencyIcon} size={size} style={{ opacity: 0.5 }} />
	);

	return (
		<StyledModalBody>
			{state?.text && <StyledDescription>{state.text}</StyledDescription>}

			<StyledInputs>
				<MoneyInput
					label="Введите сумму"
					value={amount}
					onChange={setAmount}
					icon={renderCurrencyIcon('sm')}
					max={balance}
					min={0}
					height={50}
					fontSize={16}
					padding="0 20px"
				/>
				<MoneyInput
					label={state?.balanceLabel}
					value={balance}
					onChange={() => undefined}
					icon={renderCurrencyIcon('sm')}
					disabled
					height={50}
					fontSize={16}
					padding="0 20px"
				/>
			</StyledInputs>

			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			<InputRow
				label={'Налог государства'}
				icon={<StyledPercentIcon src={percent} />}
			>
				<Text>{taxPercent.toLocaleString('ru-RU')}</Text>
			</InputRow>
			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			<InputRow label={'Вы получите'} icon={renderCurrencyIcon('md')}>
				<Text>{payout.toLocaleString('ru-RU')}</Text>
			</InputRow>

			<StyledButtonsContainer>
				<ModalButton
					$type={state?.cancelButton.color}
					onClick={() => {
						reply(false);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.cancelButton.text}
				</ModalButton>
				<ModalButton
					$type={state?.acceptButton.color}
					disabled={amount <= 0}
					onClick={() => {
						if (amount <= 0) {
							return;
						}
						reply(true);
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.acceptButton.text}
				</ModalButton>
			</StyledButtonsContainer>
		</StyledModalBody>
	);
};

export { ModalBusinessWithdraw };
